/**
 * Export Dialog Component (Reports v2)
 *
 * Pick a format (CSV / Excel / PDF / PNG) and trigger a browser download
 * by pointing window.location at the backend export endpoint
 * `/api/v1/reporting/dashboards/{id}/export/?format=<fmt>`.
 *
 * Note: the backend export endpoint is not yet wired (another agent owns
 * that). This dialog wires the UI and produces the correct URL so that
 * once the endpoint lands, downloads work without further frontend
 * changes.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  RadioGroup,
  RadioGroupItem,
} from '../../../components/ui/radio-group';
import { Download, FileSpreadsheet, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  reportingAPI,
  type AnalyseDashboard,
  type DashboardIndicator,
} from '../../../lib/api/reporting';

export type ExportFormat = 'csv' | 'xlsx' | 'pdf' | 'png';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: AnalyseDashboard | null;
  /**
   * The parent triggers the download via `reportingAPI.downloadDashboardExport`.
   * `indicatorIds` is undefined when the user left every checkbox on (export all);
   * otherwise it's the subset the user selected.
   */
  onDownload: (format: ExportFormat, applyFilters: boolean, indicatorIds?: number[]) => void;
}

const FORMAT_OPTIONS: Array<{
  value: ExportFormat;
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'xlsx',
    label: 'Excel',
    description: 'Workbook with one sheet per chart, autofilter enabled.',
    icon: <FileSpreadsheet className="h-5 w-5 text-emerald-600" />,
  },
  {
    value: 'csv',
    label: 'CSV',
    description: 'Flat tabular data export, one file per chart.',
    icon: <FileText className="h-5 w-5 text-slate-600" />,
  },
  {
    value: 'pdf',
    label: 'PDF',
    description: 'Printable layout, one page per chart with rendered images.',
    icon: <FileText className="h-5 w-5 text-rose-600" />,
  },
  {
    value: 'png',
    label: 'Chart Images (PNG)',
    description: 'Server-rendered chart images zipped together.',
    icon: <ImageIcon className="h-5 w-5 text-indigo-600" />,
  },
];

export function ExportDialog({ open, onOpenChange, report, onDownload }: ExportDialogProps) {
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [applyFilters, setApplyFilters] = useState(true);
  const [indicators, setIndicators] = useState<DashboardIndicator[]>([]);
  const [loadingIndicators, setLoadingIndicators] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open) return;
    setFormat('xlsx');
    setApplyFilters(true);
  }, [open]);

  // Load the dashboard's indicators whenever the dialog is (re)opened so the
  // user sees the current list with everything pre-selected.
  useEffect(() => {
    if (!open || !report) return;
    let cancelled = false;
    setLoadingIndicators(true);
    reportingAPI
      .getIndicators({ dashboard: report.id })
      .then((res) => {
        if (cancelled) return;
        const rows = res.data.results || [];
        setIndicators(rows);
        setSelectedIds(new Set(rows.map((r) => r.id)));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Failed to load indicators for export dialog:', err);
        toast.error('Failed to load indicator list');
      })
      .finally(() => {
        if (!cancelled) setLoadingIndicators(false);
      });
    return () => { cancelled = true; };
  }, [open, report]);

  const allSelected = indicators.length > 0 && selectedIds.size === indicators.length;
  const noneSelected = selectedIds.size === 0;

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(indicators.map((i) => i.id)));
  };

  // When every indicator is selected, send undefined so the backend falls
  // back to its default "whole dashboard" path; only send explicit IDs when
  // the user narrowed the selection.
  const effectiveIds = useMemo(
    () => (indicators.length > 0 && selectedIds.size < indicators.length
      ? Array.from(selectedIds)
      : undefined),
    [selectedIds, indicators],
  );

  const handleDownload = () => {
    if (!report) return;
    if (indicators.length > 0 && noneSelected) {
      toast.error('Select at least one chart to export');
      return;
    }
    onDownload(format, applyFilters, effectiveIds);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Export Report</DialogTitle>
          <DialogDescription>
            Download "{report?.name}" in your preferred format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Format</Label>
            <RadioGroup
              value={format}
              onValueChange={(value) => setFormat(value as ExportFormat)}
              className="gap-2"
            >
              {FORMAT_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className="flex items-start gap-3 p-3 border rounded-md"
                >
                  <RadioGroupItem
                    value={option.value}
                    id={`export-${option.value}`}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={`export-${option.value}`}
                    className="flex-1 cursor-pointer space-y-0.5"
                  >
                    <div className="flex items-center gap-2">
                      {option.icon}
                      <span className="font-medium">{option.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <label className="flex items-center gap-2 p-3 border rounded-md cursor-pointer hover:bg-muted">
            <Checkbox
              checked={applyFilters}
              onCheckedChange={(checked) => setApplyFilters(checked === true)}
            />
            <span className="text-sm">Apply current filters to the export</span>
          </label>

          {/* Per-indicator picker — hidden when the dashboard has only one chart */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Charts to include</Label>
              {indicators.length > 1 && (
                <Button type="button" variant="ghost" size="sm" onClick={toggleAll} className="h-7 text-xs">
                  {allSelected ? 'Deselect all' : 'Select all'}
                </Button>
              )}
            </div>
            {loadingIndicators ? (
              <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground border rounded-md">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading charts…
              </div>
            ) : indicators.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground border rounded-md">
                This report has no charts to export.
              </div>
            ) : (
              <div className="max-h-[180px] overflow-auto border rounded-md divide-y">
                {indicators.map((ind) => (
                  <label
                    key={ind.id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted"
                  >
                    <Checkbox
                      checked={selectedIds.has(ind.id)}
                      onCheckedChange={() => toggle(ind.id)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{ind.name}</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                        {ind.chart_type_display || ind.chart_type}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleDownload} disabled={!report}>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
